import { CompositionChildEditPreviewDto } from '@app/modules/compose/composition.types';
import { alpha, Box, Typography } from '@mui/material';

import { CompositionCardEditStripItem } from './CompositionCardEditStripItem';
import {
  COMPOSITION_CARD_EDIT_STRIP_HEIGHT_PX,
  COMPOSITION_CARD_EDIT_STRIP_MAX_VISIBLE,
} from './composition-card-edit-strip-constants';
import { getCompositionEditThumbnailSrc } from './get-composition-edit-thumbnail-src';

export interface CompositionCardEditStripProps {
  edits: CompositionChildEditPreviewDto[];
  activeIndex: number;
  onItemEnter: (index: number) => void;
  onLeave: () => void;
}

/**
 * Edit thumbnails overlaid at the bottom of the composition card media.
 * Hovering an item previews that edit in the main thumbnail (handled by the parent).
 * Only edits with a thumbnail are shown, newest-first (API order reversed); overflow "+N"
 * is rendered at the end for older items that do not fit.
 */
export function CompositionCardEditStrip({
  edits,
  activeIndex,
  onItemEnter,
  onLeave,
}: CompositionCardEditStripProps) {
  // Reverse so the default main preview (API-last thumb) appears first on the left.
  const editsWithThumbnail = edits
    .map((edit, index) => ({ edit, index }))
    .filter(({ edit }) => getCompositionEditThumbnailSrc(edit) != null)
    .reverse();

  if (editsWithThumbnail.length <= 1) {
    return null;
  }

  const maxVisible = COMPOSITION_CARD_EDIT_STRIP_MAX_VISIBLE;
  const showOverflow = editsWithThumbnail.length > maxVisible;
  // Keep the start of the reversed list visible so the default main preview stays in the strip.
  const visibleCount = showOverflow ? maxVisible - 1 : editsWithThumbnail.length;
  const visibleEdits = editsWithThumbnail.slice(0, visibleCount);
  const overflowCount = editsWithThumbnail.length - visibleCount;
  const stripHeight = COMPOSITION_CARD_EDIT_STRIP_HEIGHT_PX;

  return (
    <Box
      onMouseLeave={onLeave}
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.75,
        py: 0.75,
      }}
    >
      {visibleEdits.map(({ edit, index }) => (
        <CompositionCardEditStripItem
          key={edit.id}
          edit={edit}
          selected={index === activeIndex}
          onMouseEnter={() => onItemEnter(index)}
        />
      ))}
      {overflowCount > 0 ? (
        <Box
          sx={{
            flex: '0 0 auto',
            height: stripHeight,
            minWidth: stripHeight,
            px: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 0.5,
            bgcolor: (theme) => alpha(theme.palette.common.black, 0.55),
            color: 'common.white',
          }}
        >
          <Typography variant="caption" component="span" color="inherit">
            +{overflowCount}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}
